import * as React from 'react'
import { Loader2 } from 'lucide-react'
import type { TeamMemberDto } from '@lib/dto/team.dto'
import type { TenantRole } from '@lib/enums/tenant-role.enum'
import type { Uuid } from '@lib/validators/params'
import { canManageTeam, refreshTenantSession, useDashboardScope } from '@/dashboard/DashboardScope'
import { ReadOnlyBanner } from '@/dashboard/ReadOnlyBanner'
import { PageHeader } from '@/components/PageHeader'
import { ErrorState, LoadingState } from '@/components/PageState'
import { InvitationList } from '@/components/team/InvitationList'
import { InviteForm } from '@/components/team/InviteForm'
import { MemberTable } from '@/components/team/MemberTable'
import { ROLE_LABELS } from '@/components/team/roles'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from '@/components/ui/toast'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { useTeam, useTeamActions } from '@/hooks/useTeam'

/**
 * The organization's members and pending invitations. Everyone can see the
 * team; owners invite, change roles, and remove people, unless the dashboard
 * is read-only (suspended, or staff viewing as the tenant). The API enforces
 * the same rules, so hiding controls here is presentation, not security.
 */
export function TeamPage() {
  const scope = useDashboardScope()
  const team = useTeam()
  const actions = useTeamActions()
  const [busyId, setBusyId] = React.useState<Uuid | null>(null)
  const [actionError, setActionError] = React.useState<string | null>(null)
  const [removing, setRemoving] = React.useState<TeamMemberDto | null>(null)
  const isOwner = canManageTeam(scope)

  async function run<T>(id: Uuid, action: () => Promise<T>, success: string): Promise<boolean> {
    setBusyId(id)
    setActionError(null)
    try {
      await action()
      toast.success(success)
      return true
    } catch (error) {
      setActionError(apiErrorMessage(error))
      return false
    } finally {
      setBusyId(null)
    }
  }

  async function changeRole(member: TeamMemberDto, role: TenantRole): Promise<void> {
    const changed = await run(
      member.userId,
      () => actions.changeRole.mutateAsync({ userId: member.userId, role }),
      `${member.name} is now ${ROLE_LABELS[role]}`,
    )
    // Changing your own role changes what the dashboard should show you.
    if (changed && member.userId === scope.currentUserId) await refreshTenantSession(scope)
  }

  async function confirmRemove(): Promise<void> {
    if (!removing) return
    const member = removing
    // The dialog stays open, its buttons disabled, until the request settles.
    const removed = await run(
      member.userId,
      () => actions.remove.mutateAsync(member.userId),
      `${member.name} was removed`,
    )
    setRemoving(null)
    if (removed && member.userId === scope.currentUserId) await refreshTenantSession(scope)
  }

  const removePending = removing !== null && busyId === removing.userId

  function renderBody() {
    if (team.isLoading) return <LoadingState />
    if (team.isError || !team.data) {
      return <ErrorState message={apiErrorMessage(team.error, 'Could not load the team.')} />
    }
    const members = team.data.members
    return (
      <div className="space-y-8">
        {isOwner ? (
          <Card>
            <CardHeader>
              <CardTitle>Invite someone</CardTitle>
              <CardDescription>They&apos;ll get an email link that works for 7 days.</CardDescription>
            </CardHeader>
            <CardContent>
              <InviteForm onInvite={(input) => actions.invite.mutateAsync(input)} />
            </CardContent>
          </Card>
        ) : null}

        <section className="space-y-3">
          <h2 className="text-title font-semibold">
            Members <span className="font-normal text-muted-foreground">{members.length}</span>
          </h2>
          <MemberTable
            members={members}
            canManage={isOwner}
            {...(scope.currentUserId ? { currentUserId: scope.currentUserId } : {})}
            busyUserId={busyId}
            onChangeRole={(userId, role) => {
              const member = members.find((row) => row.userId === userId)
              if (member) void changeRole(member, role)
            }}
            onRemove={setRemoving}
          />
        </section>

        <section className="space-y-3">
          <h2 className="text-title font-semibold">Pending invitations</h2>
          <InvitationList
            invitations={team.data.invitations}
            canManage={isOwner}
            busyId={busyId}
            onResend={(id) => void run(id, () => actions.resend.mutateAsync(id), 'Invitation re-sent')}
            onRevoke={(id) => void run(id, () => actions.revoke.mutateAsync(id), 'Invitation revoked')}
          />
        </section>
      </div>
    )
  }

  return (
    <>
      <PageHeader
        title="Team"
        description={`People who can sign in to ${scope.tenantName}'s dashboard.`}
      />
      <ReadOnlyBanner />
      {actionError ? <ErrorState message={actionError} className="mb-6" /> : null}
      {renderBody()}

      <Dialog open={removing !== null} onOpenChange={(open) => !open && !removePending && setRemoving(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove {removing?.name}?</DialogTitle>
            <DialogDescription>
              {removing?.email} loses access to this organization&apos;s dashboard right away. You can
              invite them again later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" disabled={removePending} onClick={() => setRemoving(null)}>
              Cancel
            </Button>
            <Button variant="destructive" disabled={removePending} onClick={() => void confirmRemove()}>
              {removePending ? <Loader2 className="animate-spin" /> : null}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
